import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = 'https://udygtoygcqcbbkewygna.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVkeWd0b3lnY3FjYmJrZXd5Z25hIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzAxNTUwOTMsImV4cCI6MjA0NTczMTA5M30.HdHqoOzWYo9KyHxUbgnh52j-gajxvtRF_5-FUNAXw_A';

if (!supabaseUrl || !supabaseKey) {
  throw new Error('⚠️ Supabase environment variables not found!');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseKey);

// Types
export type Profile = Database['public']['Tables']['profiles']['Row'];
export type Match = Database['public']['Tables']['matches']['Row'];
export type Review = Database['public']['Tables']['reviews']['Row'];

// Database queries
export const getProfile = async (userId: string): Promise<Profile> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Profile not found');
  }

  return data as unknown as Profile;
};

export const updateProfile = async (userId: string, updates: Partial<Profile>): Promise<Profile> => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('user_id', userId)
    .select('*')
    .single();

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Profile not found');
  }

  return data as unknown as Profile;
};

export const getMatches = async (): Promise<Match[]> => {
  const { data, error } = await supabase
    .from('matches')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []) as unknown as Match[];
};

export const getMatch = async (matchId: string): Promise<Match> => {
  const { data, error } = await supabase
    .from('matches')
    .select('*')
    .eq('id', matchId)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Match not found');
  }

  return data as unknown as Match;
};

export const createMatch = async (match: Omit<Match, 'id' | 'created_at' | 'updated_at'>): Promise<Match> => {
  const { data, error } = await supabase
    .from('matches')
    .insert([match])
    .select('*')
    .single();

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Failed to create match');
  }

  return data as unknown as Match;
};

export const updateMatch = async (matchId: string, updates: Partial<Match>): Promise<Match> => {
  const { data, error } = await supabase
    .from('matches')
    .update(updates)
    .eq('id', matchId)
    .select('*')
    .single();

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Match not found');
  }

  return data as unknown as Match;
};

export const getReviews = async (userId: string): Promise<Review[]> => {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('reviewed_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? []) as unknown as Review[];
};

export const createReview = async (review: Omit<Review, 'id' | 'created_at'>): Promise<Review> => {
  const { data, error } = await supabase
    .from('reviews')
    .insert([review])
    .select('*')
    .single();

  if (error) {
    throw new Error(error.message);
  }

  if (!data) {
    throw new Error('Failed to create review');
  }

  return data as unknown as Review;
};
